import { useEffect, useMemo, useState } from "react";
import api, { formatINR } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer,
  CartesianGrid, Legend, PieChart, Pie, Cell,
} from "recharts";
import { Filter, Check, X } from "lucide-react";

const COLORS = ["#0f172a", "#2563eb", "#16a34a", "#dc2626", "#9333ea", "#f59e0b"];

export default function BI() {
  const [stats, setStats] = useState(null);
  const [trend, setTrend] = useState(null);
  const [dead, setDead] = useState({ items: [], total_value: 0 });
  const [flow, setFlow] = useState({ flow: [] });
  const [campPerf, setCampPerf] = useState({ summary: {}, campaigns: [] });
  const [days, setDays] = useState(30);

  // Phase U — multi-branch + date range filters
  const [branches, setBranches] = useState([]);
  const [selectedBranches, setSelectedBranches] = useState([]); // empty = all
  const [showBranchDropdown, setShowBranchDropdown] = useState(false);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  // Load branches once
  useEffect(() => {
    api.get("/branches").then((r) => setBranches(Array.isArray(r.data) ? r.data : (r.data?.rows || [])));
  }, []);

  const filterQuery = useMemo(() => {
    const params = new URLSearchParams();
    if (selectedBranches.length > 0) params.set("branch_ids", selectedBranches.join(","));
    if (dateFrom) params.set("date_from", dateFrom);
    if (dateTo) params.set("date_to", dateTo);
    return params.toString() ? `&${params.toString()}` : "";
  }, [selectedBranches, dateFrom, dateTo]);

  useEffect(() => {
    api.get(`/bi/overview?_t=1${filterQuery}`).then((r) => setStats(r.data));
    api.get(`/bi/revenue-trend?days=${days}${filterQuery}`).then((r) => setTrend(r.data));
    api.get(`/bi/dead-stock?days=${days}${filterQuery}`).then((r) => setDead(r.data));
    api.get(`/bi/loyalty-flow?days=${days}${filterQuery}`).then((r) => setFlow(r.data));
    api.get(`/bi/campaign-perf?_t=1${filterQuery}`).then((r) => setCampPerf(r.data));
  }, [days, filterQuery]);

  if (!stats) return <div data-testid="bi-loading">Loading...</div>;

  const tierData = Object.entries(stats.tier_distribution || {}).map(([k, v]) => ({ name: k, value: v }));
  const branchLabel = selectedBranches.length === 0
    ? "All branches"
    : selectedBranches.length === 1
      ? branches.find((b) => b.id === selectedBranches[0])?.name || "1 branch"
      : `${selectedBranches.length} branches`;

  const toggleBranch = (id) => {
    setSelectedBranches((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };
  const clearFilters = () => {
    setSelectedBranches([]);
    setDateFrom("");
    setDateTo("");
  };
  const hasFilters = selectedBranches.length > 0 || dateFrom || dateTo;

  return (
    <div className="space-y-6" data-testid="bi-page">
      {/* Sticky filter bar */}
      <div className="sticky top-0 z-20 bg-slate-50/95 backdrop-blur-sm -mx-3 sm:-mx-4 md:-mx-6 lg:-mx-8 px-3 sm:px-4 md:px-6 lg:px-8 py-3 border-b border-slate-200">
        <div className="flex flex-wrap items-center gap-2 sm:gap-3">
          <span className="text-[10px] sm:text-xs uppercase tracking-[0.2em] text-slate-500 mr-1">Window</span>
          {[7, 30, 90].map((d) => (
            <button key={d} onClick={() => setDays(d)} className={`text-xs px-3 py-1 border ${days === d ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-300"}`} data-testid={`bi-days-${d}`}>{d}d</button>
          ))}

          {/* Branch multi-select */}
          {branches.length > 1 && (
            <div className="relative">
              <button
                onClick={() => setShowBranchDropdown((s) => !s)}
                className={`text-xs px-3 py-1 border inline-flex items-center gap-1.5 ${hasFilters && selectedBranches.length > 0 ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-300"}`}
                data-testid="bi-branch-dropdown-btn"
              >
                <Filter size={12} />
                {branchLabel}
              </button>
              {showBranchDropdown && (
                <div className="absolute top-full mt-1 left-0 z-50 bg-white border border-slate-300 shadow-lg w-64 max-h-72 overflow-y-auto" data-testid="bi-branch-dropdown-menu">
                  <button
                    onClick={() => { setSelectedBranches([]); }}
                    className={`w-full text-left px-3 py-2 text-xs border-b border-slate-100 ${selectedBranches.length === 0 ? "bg-slate-100 font-semibold" : "hover:bg-slate-50"}`}
                    data-testid="bi-branch-select-all"
                  >
                    {selectedBranches.length === 0 && <Check size={12} className="inline mr-1.5" />}
                    All branches
                  </button>
                  {branches.map((b) => (
                    <label
                      key={b.id}
                      className="w-full text-left px-3 py-2 text-xs hover:bg-slate-50 flex items-center gap-2 cursor-pointer"
                      data-testid={`bi-branch-opt-${b.id}`}
                    >
                      <input
                        type="checkbox"
                        checked={selectedBranches.includes(b.id)}
                        onChange={() => toggleBranch(b.id)}
                        className="h-3.5 w-3.5"
                      />
                      <span className="flex-1 truncate">{b.name}</span>
                      {b.business_category && (
                        <span className="text-[10px] text-slate-400 uppercase">{b.business_category}</span>
                      )}
                    </label>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Date range */}
          <div className="flex items-center gap-1.5 ml-auto sm:ml-0">
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="text-xs h-7 px-2 border border-slate-300 w-32"
              data-testid="bi-date-from"
            />
            <span className="text-xs text-slate-400">→</span>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="text-xs h-7 px-2 border border-slate-300 w-32"
              data-testid="bi-date-to"
            />
          </div>

          {hasFilters && (
            <button
              onClick={clearFilters}
              className="text-xs px-2 py-1 text-slate-500 hover:text-slate-900 inline-flex items-center gap-1"
              data-testid="bi-clear-filters"
            >
              <X size={12} /> Clear
            </button>
          )}
        </div>
      </div>

      <Tabs defaultValue="overview" data-testid="bi-tabs">
        <TabsList className="rounded-none flex-wrap h-auto">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="trend" data-testid="bi-tab-trend">Revenue Trend</TabsTrigger>
          <TabsTrigger value="deadstock" data-testid="bi-tab-deadstock">Dead Stock</TabsTrigger>
          <TabsTrigger value="loyalty" data-testid="bi-tab-loyalty">Loyalty Flow</TabsTrigger>
          <TabsTrigger value="campaigns" data-testid="bi-tab-campaigns">Campaign Perf</TabsTrigger>
          <TabsTrigger value="user-perf" data-testid="bi-tab-user-perf">User Sales</TabsTrigger>
          <TabsTrigger value="credits-pending" data-testid="bi-tab-credits-pending">Credits Pending</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <KPI label="Revenue · 7d" value={formatINR(stats.revenue_week)} />
            <KPI label="Net profit (est.)" value={formatINR(stats.net_profit)} />
            <KPI label="Gross margin" value={`${stats.gross_margin_pct}%`} />
            <KPI label="Inventory value" value={formatINR(stats.inventory_value)} />
            <KPI label="Customers" value={stats.total_customers} />
            <KPI label="Pending approvals" value={stats.pending_approvals} />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card className="rounded-sm p-6 border-slate-200">
              <div className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-3">Per-Branch Revenue</div>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={stats.per_branch}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="branch_name" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(v) => formatINR(v)} />
                  <Bar dataKey="revenue" fill="#2563eb" />
                </BarChart>
              </ResponsiveContainer>
            </Card>
            <Card className="rounded-sm p-6 border-slate-200">
              <div className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-3">Loyalty Tier Distribution</div>
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie data={tierData} dataKey="value" nameKey="name" outerRadius={80} label>
                    {tierData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </Card>
          </div>
          <Card className="rounded-sm p-6 border-slate-200">
            <div className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-3">Top 10 Products by Revenue</div>
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200"><tr><th className="p-2 text-left">Product</th><th className="p-2 text-right">Qty Sold</th><th className="p-2 text-right">Revenue</th></tr></thead>
              <tbody>
                {stats.top_products.length === 0 && <tr><td colSpan={3} className="p-6 text-center text-slate-500">No sales data yet.</td></tr>}
                {stats.top_products.map((p) => (
                  <tr key={p.product_id} className="border-b border-slate-100">
                    <td className="p-2">{p.name}</td>
                    <td className="p-2 text-right tabular-nums">{p.qty}</td>
                    <td className="p-2 text-right tabular-nums font-semibold">{formatINR(p.revenue)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </TabsContent>

        <TabsContent value="trend">
          <Card className="rounded-sm p-6 border-slate-200">
            <div className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-3">Revenue Trend per Branch · {days}d</div>
            {trend && trend.trend.length > 0 ? (
              <ResponsiveContainer width="100%" height={360}>
                <LineChart data={trend.trend}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(v) => formatINR(v)} />
                  <Legend />
                  {(trend.branches || []).map((b, i) => (
                    <Line key={b} type="monotone" dataKey={b} stroke={COLORS[i % COLORS.length]} strokeWidth={2} dot={false} />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            ) : <div className="text-center text-slate-500 py-12">No data.</div>}
          </Card>
        </TabsContent>

        <TabsContent value="deadstock">
          <Card className="rounded-sm p-6 border-slate-200">
            <div className="flex justify-between items-center mb-3">
              <div className="text-xs uppercase tracking-[0.2em] text-slate-500">Dead Stock · {days}d (no sales)</div>
              <div className="text-sm font-semibold tabular-nums">Total locked: {formatINR(dead.total_value || 0)}</div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="deadstock-table">
                <thead className="bg-slate-50 border-b border-slate-200"><tr>
                  <th className="p-2 text-left">Product</th>
                  <th className="p-2 text-left">Barcode</th>
                  <th className="p-2 text-left">Category</th>
                  <th className="p-2 text-right">Stock</th>
                  <th className="p-2 text-right">Locked Value</th>
                </tr></thead>
                <tbody>
                  {(dead.items || []).length === 0 && <tr><td colSpan={5} className="p-6 text-center text-slate-500">No dead stock 🎉</td></tr>}
                  {(dead.items || []).map((p) => (
                    <tr key={p.id} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="p-2">{p.name}</td>
                      <td className="p-2 font-mono text-xs">{p.barcode}</td>
                      <td className="p-2 text-slate-600">{p.category}</td>
                      <td className="p-2 text-right tabular-nums">{p.stock}</td>
                      <td className="p-2 text-right tabular-nums font-semibold">{formatINR(p.value)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="loyalty">
          <Card className="rounded-sm p-6 border-slate-200">
            <div className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-3">Loyalty Flow · {days}d</div>
            {flow.flow && flow.flow.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={flow.flow}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="earned" fill="#16a34a" name="Earned" />
                  <Bar dataKey="redeemed" fill="#2563eb" name="Redeemed" />
                  <Bar dataKey="returned" fill="#dc2626" name="Returned" />
                </BarChart>
              </ResponsiveContainer>
            ) : <div className="text-center text-slate-500 py-12">No loyalty activity yet.</div>}
          </Card>
        </TabsContent>

        <TabsContent value="campaigns">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
            <KPI label="Total" value={campPerf.summary?.total || 0} />
            <KPI label="Sent" value={campPerf.summary?.sent || 0} />
            <KPI label="Pending" value={campPerf.summary?.pending || 0} />
            <KPI label="Rejected" value={campPerf.summary?.rejected || 0} />
            <KPI label="Spend" value={formatINR(campPerf.summary?.estimated_spend || 0)} />
          </div>
          <Card className="rounded-sm p-6 border-slate-200">
            <table className="w-full text-sm" data-testid="campaign-perf-table">
              <thead className="bg-slate-50 border-b border-slate-200"><tr>
                <th className="p-2 text-left">Name</th>
                <th className="p-2 text-left">Kind</th>
                <th className="p-2 text-left">Status</th>
                <th className="p-2 text-right">Recipients</th>
                <th className="p-2 text-right">Sent</th>
              </tr></thead>
              <tbody>
                {(campPerf.campaigns || []).length === 0 && <tr><td colSpan={5} className="p-6 text-center text-slate-500">No campaigns yet.</td></tr>}
                {(campPerf.campaigns || []).map((c) => (
                  <tr key={c.id} className="border-b border-slate-100">
                    <td className="p-2">{c.name}</td>
                    <td className="p-2 capitalize text-slate-600 text-xs">{c.kind || c.target?.replace("_", " ")}</td>
                    <td className="p-2 capitalize">{c.status?.replace("_", " ")}</td>
                    <td className="p-2 text-right tabular-nums">{c.estimated_recipients}</td>
                    <td className="p-2 text-right tabular-nums">{c.sent_count || 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </TabsContent>

        <TabsContent value="user-perf" className="space-y-4"><UserPerformanceTab /></TabsContent>
        <TabsContent value="credits-pending" className="space-y-4"><CreditsPendingTab /></TabsContent>
      </Tabs>
    </div>
  );
}

function UserPerformanceTab() {
  const [data, setData] = useState(null);
  const [filters, setFilters] = useState({ start: "", end: "", branch_id: "", user_id: "" });
  useEffect(() => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v) params.append(k, v); });
    api.get(`/bi/user-sales-performance?${params.toString()}`).then((r) => setData(r.data));
  }, [filters]);
  if (!data) return <div className="text-sm text-slate-500">Loading...</div>;
  const s = data.summary || {};
  return (
    <div className="space-y-4" data-testid="user-perf-tab">
      <div className="flex gap-2 text-xs">
        <input type="date" value={filters.start} onChange={(e) => setFilters({ ...filters, start: e.target.value })} className="border border-slate-300 rounded-none px-2 py-1" data-testid="up-start" />
        <input type="date" value={filters.end} onChange={(e) => setFilters({ ...filters, end: e.target.value })} className="border border-slate-300 rounded-none px-2 py-1" data-testid="up-end" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <KPI label="Total Revenue" value={formatINR(s.total_revenue || 0)} />
        <KPI label="Total Bills" value={s.total_bills || 0} />
        <KPI label="Total Users" value={s.total_users || 0} />
        <KPI label="Top User" value={s.top_user || "—"} />
        <KPI label="Top User Revenue" value={formatINR(s.top_user_revenue || 0)} />
        <KPI label="Credit Given" value={formatINR(s.total_credit_given || 0)} />
      </div>
      <Card className="rounded-sm p-4 border-slate-200" data-testid="user-perf-bar">
        <div className="text-xs uppercase tracking-widest text-slate-500 mb-2">Revenue per User</div>
        <ResponsiveContainer width="100%" height={250}>
          <BarChart data={data.rows}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="user_name" />
            <YAxis />
            <Tooltip />
            <Bar dataKey="revenue" fill="#0f172a" />
          </BarChart>
        </ResponsiveContainer>
      </Card>
      <Card className="rounded-sm p-4 border-slate-200">
        <table className="w-full text-sm" data-testid="user-perf-table">
          <thead className="bg-slate-50 border-b border-slate-200"><tr>
            <th className="p-2 text-left">User</th><th className="p-2 text-left">Role</th><th className="p-2 text-left">Branch</th>
            <th className="p-2 text-right">Bills</th><th className="p-2 text-right">Revenue</th>
            <th className="p-2 text-right">Avg Bill</th><th className="p-2 text-right">Returns</th>
            <th className="p-2 text-right">Credit Given</th><th className="p-2 text-right">Split Bills</th>
          </tr></thead>
          <tbody>
            {data.rows.length === 0 && <tr><td colSpan={9} className="p-6 text-center text-slate-500">No data.</td></tr>}
            {data.rows.map((r) => (
              <tr key={r.user_id} className="border-b border-slate-100" data-testid={`up-row-${r.user_id}`}>
                <td className="p-2">{r.user_name}</td>
                <td className="p-2 text-xs text-slate-600 capitalize">{(r.role || "").replace("_", " ")}</td>
                <td className="p-2 text-xs text-slate-600">{r.branch_name}</td>
                <td className="p-2 text-right tabular-nums">{r.bills_count}</td>
                <td className="p-2 text-right tabular-nums font-semibold">{formatINR(r.revenue)}</td>
                <td className="p-2 text-right tabular-nums">{formatINR(r.average_bill_value)}</td>
                <td className="p-2 text-right tabular-nums">{r.returns_count}</td>
                <td className="p-2 text-right tabular-nums">{formatINR(r.credit_given)}</td>
                <td className="p-2 text-right tabular-nums">{r.split_bills}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

function CreditsPendingTab() {
  const [data, setData] = useState(null);
  const [filters, setFilters] = useState({ start: "", end: "", branch_id: "" });
  useEffect(() => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v) params.append(k, v); });
    api.get(`/bi/credits-pending?${params.toString()}`).then((r) => setData(r.data));
  }, [filters]);
  if (!data) return <div className="text-sm text-slate-500">Loading...</div>;
  const c = data.customers || { rows: [], kpis: {} };
  const p = data.parties || { rows: [], kpis: {} };
  return (
    <div className="space-y-4" data-testid="credits-pending-tab">
      <div className="flex gap-2 text-xs">
        <input type="date" value={filters.start} onChange={(e) => setFilters({ ...filters, start: e.target.value })} className="border border-slate-300 rounded-none px-2 py-1" data-testid="cp-start" />
        <input type="date" value={filters.end} onChange={(e) => setFilters({ ...filters, end: e.target.value })} className="border border-slate-300 rounded-none px-2 py-1" data-testid="cp-end" />
      </div>
      <div className="text-sm font-bold uppercase tracking-widest text-slate-500">Customer Credits</div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <KPI label="Outstanding" value={formatINR(c.kpis.total_outstanding || 0)} />
        <KPI label="Full Credit Bills" value={c.kpis.full_credit_bills || 0} />
        <KPI label="Split Bills" value={c.kpis.split_payment_bills || 0} />
        <KPI label="Collected this Month" value={formatINR(c.kpis.collected_this_month || 0)} />
        <KPI label="Oldest (days)" value={c.kpis.oldest_days || 0} />
      </div>
      <Card className="rounded-sm p-4 border-slate-200">
        <table className="w-full text-sm" data-testid="cp-customer-table">
          <thead className="bg-slate-50 border-b border-slate-200"><tr>
            <th className="p-2 text-left">Customer</th><th className="p-2 text-left">Phone</th>
            <th className="p-2 text-right">Total Credit</th><th className="p-2 text-right">Upfront Paid</th>
            <th className="p-2 text-right">Paid Later</th><th className="p-2 text-right">Outstanding</th>
            <th className="p-2 text-right">Days</th>
          </tr></thead>
          <tbody>
            {c.rows.length === 0 && <tr><td colSpan={7} className="p-6 text-center text-slate-500">No customer credits outstanding.</td></tr>}
            {c.rows.map((r) => (
              <tr key={r.customer_id} className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer"
                onClick={() => window.location.assign(`/credits`)} data-testid={`cp-cust-${r.customer_id}`}>
                <td className="p-2 font-medium">{r.customer_name}</td>
                <td className="p-2 text-slate-600">{r.customer_phone}</td>
                <td className="p-2 text-right tabular-nums">{formatINR(r.total_credit)}</td>
                <td className="p-2 text-right tabular-nums">{formatINR(r.amount_paid_upfront)}</td>
                <td className="p-2 text-right tabular-nums">{formatINR(r.amount_paid_later)}</td>
                <td className="p-2 text-right tabular-nums font-bold text-orange-600">{formatINR(r.outstanding)}</td>
                <td className="p-2 text-right tabular-nums">{r.days_outstanding}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
      <div className="text-sm font-bold uppercase tracking-widest text-slate-500 pt-4">Party / Supplier Payables</div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <KPI label="Outstanding" value={formatINR(p.kpis.total_outstanding || 0)} />
        <KPI label="Parties with Dues" value={p.kpis.parties_with_dues || 0} />
        <KPI label="Oldest (days)" value={p.kpis.oldest_days || 0} />
      </div>
      <Card className="rounded-sm p-4 border-slate-200">
        <table className="w-full text-sm" data-testid="cp-party-table">
          <thead className="bg-slate-50 border-b border-slate-200"><tr>
            <th className="p-2 text-left">Party</th>
            <th className="p-2 text-right">Purchase Total</th>
            <th className="p-2 text-right">Paid</th>
            <th className="p-2 text-right">Outstanding</th>
            <th className="p-2 text-right">Days</th>
          </tr></thead>
          <tbody>
            {p.rows.length === 0 && <tr><td colSpan={5} className="p-6 text-center text-slate-500">No supplier payables outstanding.</td></tr>}
            {p.rows.map((r) => (
              <tr key={r.party_id} className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer"
                onClick={() => window.location.assign(`/parties`)} data-testid={`cp-party-${r.party_id}`}>
                <td className="p-2 font-medium">{r.party_name}</td>
                <td className="p-2 text-right tabular-nums">{formatINR(r.total_purchase)}</td>
                <td className="p-2 text-right tabular-nums">{formatINR(r.amount_paid)}</td>
                <td className="p-2 text-right tabular-nums font-bold text-orange-600">{formatINR(r.outstanding)}</td>
                <td className="p-2 text-right tabular-nums">{r.days_outstanding}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

function KPI({ label, value }) {
  return (
    <Card className="rounded-sm border-slate-200 p-4">
      <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">{label}</div>
      <div className="text-xl font-bold tabular-nums mt-1" style={{ fontFamily: "Cabinet Grotesk, IBM Plex Sans, sans-serif" }}>{value}</div>
    </Card>
  );
}
